/* The plain words this console uses for each internet line's data budget and
 * how much the box may use the line (cloud-anchor/pkg/posture). The appliance
 * decides and counts; these words only say what it decided and counted.
 *
 * A budget is in decimal gigabytes, the way a data plan is sold. */

import { fmtBytesDecimal } from "../utils/formatters";

export const TITLE = "Data Budgets";

/* What an operator can choose for a line, in the order the form offers it. */
export const USE_CHOICES = [
  ["active", "Use it normally"],
  ["standby", "Only as a backup"],
  ["cold", "Switch it off"],
];

/* The defaults a budget starts from: told at 80%, a backup at 90%, off at
   100%. Each can be cleared. */
export const DEFAULT_RUNGS = { alert_pct: 80, standby_pct: 90, cold_pct: 100 };

export const RUNGS = [
  ["alert_pct", "Alert at", "Everyone on the site's alert list is emailed."],
  ["standby_pct", "Backup only at", "It carries traffic only when the other lines are down."],
  ["cold_pct", "Switch off at", "It sends nothing at all until the month starts again."],
];

/* Said wherever a line can be switched off, in the form and in the
   confirmation: off is radio silence, and it cuts more than traffic. */
export const OFF_MEANS =
  "Off means nothing at all leaves by this line, not even replies: no traffic, no checks, nothing from the box itself. " +
  "Connections open on it break, and anyone who reaches this box over the network this line plugs into, " +
  "an office network it also serves for example, loses the box until the line is back on. " +
  "If every other line is down at the same time, this site is offline and cannot be reached from here.";

export const BACKUP_MEANS =
  "A backup line is checked so it is ready, and carries traffic only while every other line is down. " +
  "It never joins the others to add speed.";

export const COUNTS_WHAT =
  "Usage is everything this line sent and received, counted on the box the way the carrier bills it.";

/* A rate as the appliance writes it ("1mbit") the way people read one. "" when
   the appliance does not say, which is a build that switches even its last
   line off. */
export function rateWords(rate) {
  const m = /^(\d+(?:\.\d+)?)([kmg])bit$/i.exec(rate || "");
  return m ? `${m[1]} ${m[2].toUpperCase()}bit/s` : rate || "";
}

/* Whether no other line of the box is on: a budget slows such a line rather
   than switch it off, since the console reaches the box through it. */
export function lastLineOn(lines, line) {
  return !lines.some((l) => l.name !== line.name && l.present && l.posture !== "cold");
}

/* Said in the form wherever a budget could run out on the box's last line on. */
export function slowMeans(rate) {
  return `This is the box's only line on, so its budget never switches it off. When the month's usage reaches the ` +
    `switch-off point, the line is slowed to ${rate} both ways until the month starts again: you can still reach ` +
    `the box, but everything at the site crawls.`;
}

/* Why a budget being saved acts at once: the line has already used more. */
export function actsNowWords(line, offAt, slowsTo) {
  const what = slowsTo
    ? `It is the box's only line on, so it is slowed to ${slowsTo} both ways, at once, until ${day(line.usage.next_cycle)}. ` +
      "Everything at the site will crawl."
    : `It is switched off at once, until ${day(line.usage.next_cycle)}. ${OFF_MEANS}`;
  return `It has already used ${gb(line.usage.bytes)} this month, past the ${gb(offAt)} you are setting. ${what}`;
}

/* The alarm a slowed line raises at the top of the screen. */
export function slowedAlarm(line, rate, wanName) {
  return {
    title: `${wanName(line.name)} is slowed to ${rate}: its data budget is used up`,
    detail: `${gb(line.usage.bytes)} used of a ${gb(line.setting?.budget?.limit_bytes)} budget. It is the box's only ` +
      `line on, so it was slowed rather than switched off: you can still reach the box, but everything at the site ` +
      `is very slow until ${day(line.usage.next_cycle)}. To lift it now, raise the budget below, or bring another line on.`,
  };
}

const GB = 1e9;

/* An amount the way the carrier bills it, in decimal units: GB, and TB once
   a line has carried a thousand of them. */
export const gb = (bytes) => fmtBytesDecimal(bytes || 0);

export function day(ts) {
  return ts ? new Date(ts).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";
}

/* The usage at which a rung takes effect, 0 when it is not set. */
export function rungBytes(budget, key) {
  const pct = budget?.[key] || 0;
  return pct > 0 ? (budget.limit_bytes * pct) / 100 : 0;
}

export function usedPct(line) {
  const limit = line.setting?.budget?.limit_bytes;
  return limit ? Math.round((100 * line.usage.bytes) / limit) : null;
}

/* The line's state as a short label and a tone the screen colours it by. */
export function stateChip(line) {
  if (line.throttled) return { label: "Slowed", tone: "slowed" };
  if (line.posture === "cold") return { label: "Off", tone: "off" };
  if (line.posture === "standby") return { label: "Backup only", tone: "backup" };
  return { label: "In use", tone: "on" };
}

/* Why the line is as it is, one sentence. Empty for a line in use with nothing
   to say about it. */
export function why(line, rate) {
  const reset = day(line.usage?.next_cycle);
  const pct = usedPct(line);
  if (line.throttled) {
    return `Its data budget is used up. It is the box's only line on, so it is slowed to ${rate} both ways ` +
      `instead of switched off, until ${reset}.`;
  }
  if (line.posture === "cold") {
    return line.cause === "budget"
      ? `Its data budget is used up, so it sends nothing until ${reset}.`
      : "You switched it off. It sends nothing at all.";
  }
  if (line.posture === "standby") {
    return line.cause === "budget"
      ? `Its data budget is ${pct}% used, so it carries traffic only when the other lines are down, until ${reset}.`
      : "You keep it as a backup: it carries traffic only when the other lines are down.";
  }
  if (line.alert) return `Its data budget is ${pct}% used.`;
  return "";
}

/* The usage line under a line's name. */
export function usageWords(line) {
  const used = `${gb(line.usage.bytes)} used since ${day(line.usage.cycle_start)}`;
  const b = line.setting?.budget;
  if (!b) return `${used}. No budget set.`;
  return `${used}, of a ${gb(b.limit_bytes)} budget. It starts again on ${day(line.usage.next_cycle)}.`;
}

/* What happens next as the line keeps carrying traffic, for a line with a
   budget, in the order it will happen. */
export function nextWords(line, slowsTo) {
  const b = line.setting?.budget;
  if (!b) return "";
  const used = line.usage.bytes;
  const out = [];
  const standby = rungBytes(b, "standby_pct");
  const cold = rungBytes(b, "cold_pct");
  if (standby > used && line.posture === "active") out.push(`at ${gb(standby)} it becomes a backup only`);
  if (cold > used && line.posture !== "cold") {
    out.push(slowsTo ? `at ${gb(cold)} it is slowed to ${slowsTo}, as the box's only line on` : `at ${gb(cold)} it is switched off`);
  }
  if (!out.length) return "";
  const s = out.join(", and ");
  return `${s[0].toUpperCase()}${s.slice(1)}.`;
}

/* The measurements behind the usage line. */
export function measureWords(line, fmt = gb) {
  const u = line.usage;
  const out = [`Received ${fmt(u.rx_bytes)}, sent ${fmt(u.tx_bytes)}.`];
  if (new Date(u.since) > new Date(u.cycle_start)) {
    out.push(`Counted since ${day(u.since)}: the box began counting this line then, so anything before is not in this figure.`);
  } else if (new Date(u.since) < new Date(u.cycle_start)) {
    out.push(`Counted since ${day(u.since)}: the month's start day moved, and what was already counted was kept.`);
  }
  if (u.prev_cycle_start) out.push(`The month before, from ${day(u.prev_cycle_start)}: ${fmt(u.prev_bytes)}.`);
  return out;
}

/* The screen's headline: what the operator would ask first, answered. One
   sentence for each line that is not simply in use, and why. */
export function headline(lines, wanName, rate) {
  const out = [];
  let tone = "good";
  for (const l of lines.filter((x) => x.present)) {
    const name = wanName(l.name);
    const reset = day(l.usage?.next_cycle);
    if (l.throttled) {
      tone = "bad";
      out.push(`${name} is slowed to ${rate} until ${reset}: its data budget is used up.`);
    } else if (l.posture === "cold") {
      tone = "bad";
      out.push(l.cause === "budget"
        ? `${name} is switched off until ${reset}: its data budget is used up.`
        : `${name} is switched off.`);
    } else if (l.posture === "standby") {
      out.push(l.cause === "budget"
        ? `${name} is kept as a backup until ${reset}: its data budget is ${usedPct(l)}% used.`
        : `${name} is kept as a backup.`);
    } else if (l.alert) {
      out.push(`${name} has used ${usedPct(l)}% of its data budget.`);
    }
  }
  if (!out.length) return { tone, text: "Every line is in use, and none is near its budget." };
  return { tone: tone === "bad" ? "bad" : "warn", text: out.join(" ") };
}

/* A form's text to the setting the edge takes: a blank budget is none, a blank
   rung is never. */
export function settingFromForm(form) {
  const limit = String(form.limit_gb).trim();
  if (limit === "") return { posture: form.posture, budget: null };
  const pct = (v) => (String(v).trim() === "" ? 0 : Number(v));
  return {
    posture: form.posture,
    budget: {
      limit_bytes: Math.round(Number(limit) * GB),
      cycle_day: Number(form.cycle_day),
      alert_pct: pct(form.alert_pct),
      standby_pct: pct(form.standby_pct),
      cold_pct: pct(form.cold_pct),
    },
  };
}

/* A line's setting as the form edits it. */
export function formFromSetting(setting) {
  const b = setting?.budget;
  const str = (v) => (v ? String(v) : "");
  return {
    posture: setting?.posture || "active",
    limit_gb: b ? String(b.limit_bytes / GB) : "",
    cycle_day: b ? b.cycle_day : 1,
    alert_pct: b ? str(b.alert_pct) : String(DEFAULT_RUNGS.alert_pct),
    standby_pct: b ? str(b.standby_pct) : String(DEFAULT_RUNGS.standby_pct),
    cold_pct: b ? str(b.cold_pct) : String(DEFAULT_RUNGS.cold_pct),
  };
}
